import {
  IProgressIndicatorProps,
  IProgressIndicatorStyles,
  ISpinnerProps,
  ISpinnerStyles,
  ProgressIndicator,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import { forwardRef, HTMLAttributes, Ref } from 'react';

import { IStyleFunctionOrObject } from '../../models/components.models';
import { useClassNames, useThemeStyles } from '../../utils/hooks';
import { combineClassNames } from '../../utils/utils';
import {
  ILoaderContainerClassnames,
  ILoaderContainerStyles,
  loaderContainerStylesDefault,
  loaderStylesProgressIndicatorDefault,
  loaderStylesSpinnerDefault,
} from './Loader.styles';

export enum LoaderType {
  spinner = 'spinner',
  progressIndicator = 'progressIndicator',
}

export interface ILoaderProps extends HTMLAttributes<HTMLDivElement> {
  type?: LoaderType;
  label?: string;
  size?: SpinnerSize;
  description?: string;
  percentComplete?: number;
  'data-test'?: string;
  styles?:
    | IStyleFunctionOrObject<ISpinnerStyles>
    | IStyleFunctionOrObject<ISpinnerStyles>[]
    | IStyleFunctionOrObject<IProgressIndicatorStyles>
    | IStyleFunctionOrObject<IProgressIndicatorStyles>[];
  loaderContainerStyles?:
    | IStyleFunctionOrObject<ILoaderContainerStyles>
    | IStyleFunctionOrObject<ILoaderContainerStyles>[];
}

export const Loader = forwardRef((props: ILoaderProps, ref: Ref<HTMLDivElement>) => {
  const {
    percentComplete,
    type = percentComplete !== undefined ? LoaderType.progressIndicator : LoaderType.spinner,
    label,
    size,
    description,
    styles,
    loaderContainerStyles,
    ...elementProps
  } = props;
  const { className } = props;
  const loaderStyles = useThemeStyles(
    type === LoaderType.progressIndicator ? loaderStylesProgressIndicatorDefault : loaderStylesSpinnerDefault,
    styles
  );
  const renderLoader = () => {
    switch (type) {
      case LoaderType.progressIndicator: {
        const piProps: IProgressIndicatorProps = {
          className: combineClassNames(className, percentComplete !== undefined && 'ProgressIndicator-percent'),
          styles: loaderStyles,
          label,
          description,
          percentComplete,
        };
        return <ProgressIndicator {...piProps} />;
      }
      default: {
        const sProps: ISpinnerProps = {
          className,
          styles: loaderStyles,
          label,
          size,
        };
        return <Spinner {...sProps} />;
      }
    }
  };
  const classNames = useClassNames<ILoaderContainerStyles, ILoaderContainerClassnames>(
    'LoaderContainer',
    loaderContainerStylesDefault,
    loaderContainerStyles,
    className
  );
  return (
    <div {...elementProps} className={classNames.root} ref={ref} data-test={props['data-test'] || 'loader'}>
      {renderLoader()}
    </div>
  );
});
